/**
 * Converts a route URL into one consistent format used by tabs and the route cache.
 *
 * It removes extra spaces, adds a leading slash, and sorts query parameters.
 * For example, `?a=1&b=2` and `?b=2&a=1` become the same cache key.
 *
 * This lets the app find and restore the correct page when a tab is reopened.
 */
export function normalizeTabRouteKey(url: string): string {
    const trimmed = url.trim();
    const qIdx = trimmed.indexOf('?');
    // No query string: just normalize the leading slash.
    if (qIdx === -1) {
        const p = trimmed.length > 0 ? trimmed : '/';
        return p.startsWith('/') ? p : `/${p}`;
    }
    const pathPart = trimmed.slice(0, qIdx);
    const queryPart = trimmed.slice(qIdx + 1);
    const path = pathPart.length > 0 ? pathPart : '/';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Sort keys so param order never affects the resulting cache key.
    const params = new URLSearchParams(queryPart);
    const keys = [...params.keys()].sort();
    if (keys.length === 0) {
        return normalizedPath;
    }
    const qs = keys
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params.get(k) ?? '')}`)
        .join('&');
    return `${normalizedPath}?${qs}`;
}
